/**
 * The feed: what just happened, newest first, as short lines with the time in the margin and the noun's
 * icon — a run, a document, a record — carrying a dot when something is wrong. Identifiers are mono; the
 * one a line is about links into the stage. Every line opens the thing. Lines that arrive while the page
 * is open rise in.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router';
import { useEnv } from '../lib/envs.tsx';
import { useClock } from './useLive.ts';
import { Kicker, NounIcon, cx } from '../design/primitives.tsx';
import { PinIcon, PulseIcon } from '../components/icons.tsx';
import type { FeedAbout, FeedLine, FeedPart } from './feedLines.ts';

const DOT: Record<NonNullable<FeedLine['tone']>, string> = { fail: 'bg-fail', warn: 'bg-warn', ok: 'bg-ok' };

export function Part({ part }: { part: FeedPart }) {
  const { link } = useEnv();
  if (typeof part === 'string') return part;
  if (part.subject) {
    return <Link to={link(`/s?${new URLSearchParams({ q: part.mono })}`)} className="mono" onClick={(e) => e.stopPropagation()}>{part.mono}</Link>;
  }
  return <span className="mono">{part.mono}</span>;
}

/**
 * The words of a line, with the ones that name its integration as a button (its peek) when the page asks for that:
 * the first string part holding the phrase is split around it. Everything else renders as before.
 */
function LineText({ line, onThing }: { line: FeedLine; onThing?: (about: FeedAbout) => void }) {
  const about = line.about;
  let done = !about || !onThing;
  return (
    <>
      {line.parts.map((p, i) => {
        if (done || typeof p !== 'string' || !about) return <Part key={i} part={p} />;
        const at = p.indexOf(about.phrase);
        if (at < 0) return <Part key={i} part={p} />;
        done = true;
        return (
          <span key={i}>
            {p.slice(0, at)}
            <button type="button" className="now-thing" title={`Integration: ${about.process}`}
              onClick={(e) => { e.stopPropagation(); onThing!(about); }} onKeyDown={(e) => e.stopPropagation()}>{about.phrase}</button>
            {p.slice(at + about.phrase.length)}
          </span>
        );
      })}
    </>
  );
}

function dayLabel(at: number, now: number): string | null {
  const d = dayjs(at);
  if (d.isSame(dayjs(now), 'day')) return null;
  if (d.isSame(dayjs(now).subtract(1, 'day'), 'day')) return 'Yesterday';
  return d.format('ddd D MMM');
}

function Clock() {
  const now = useClock(1000);
  return <time dateTime={dayjs(now).toISOString()} className="tabular-nums">{dayjs(now).format('HH:mm:ss')}</time>;
}

export function Feed({ lines, now, loading, where, onOpen, onThing, pin }: {
  lines: FeedLine[]; now: number; loading?: boolean; where?: string; onOpen: (line: FeedLine) => void;
  /** The pin that keeps the feed open (else it opens while pointed at): whether it is pinned and how to toggle it. */
  pin?: { on: boolean; toggle: () => void };
  /** The integration a line names, pressed: its words become a button when given. */
  onThing?: (about: FeedAbout) => void;
}) {
  // Lines seen in an earlier render do not animate; the ones that arrive do
  const seen = useRef<Set<string> | null>(null);
  const keys = new Set(lines.map((l) => l.key));
  const isNew = (key: string) => seen.current !== null && !seen.current.has(key);
  useEffect(() => {
    seen.current = keys;
  });

  const keyActivate = (line: FeedLine) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(line);
    }
  };

  let day: string | null = null;
  return (
    <aside className="now-feed" aria-label="Just happened" aria-busy={loading || undefined}>
      <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-2 shrink-0">
        <Kicker>Just happened</Kicker>
        <span className="inline-flex items-center gap-1.5 font-mono text-micro text-n7 min-w-0">
          <i className={cx('size-1.5 rounded-full shrink-0', loading ? 'bg-n5' : 'bg-ok now-live')} aria-hidden="true" />
          {where && <span className="truncate">{where} ·</span>}
          <Clock />
          {pin && (
            <button type="button" onClick={pin.toggle} aria-pressed={pin.on} data-feed-pin=""
              title={pin.on ? 'Unpin: the feed opens while pointed at' : 'Pin the feed open'}
              className={cx('ml-1 -mr-1.5 grid place-items-center size-7 rounded-full border-0 cursor-pointer transition-colors duration-[var(--t-micro)]',
                pin.on ? 'bg-accent-soft text-accent' : 'bg-transparent text-n7 hover:bg-n3 hover:text-n10')}>
              <PinIcon size={15} className={pin.on ? '' : 'rotate-45'} />
            </button>
          )}
        </span>
      </div>
      <div className="now-feed-list">
        {loading && lines.length === 0 && Array.from({ length: 9 }, (_, i) => (
          <div key={i} className="now-row" aria-hidden="true">
            <span className="mv-shimmer rounded-sm h-[10px] w-[32px] mt-1" />
            <span className="mv-shimmer rounded-full size-[18px]" />
            <span className="flex flex-col gap-1.5 pt-0.5">
              <span className="mv-shimmer rounded-sm h-[11px]" style={{ width: `${62 + ((i * 29) % 36)}%` }} />
              {i % 3 !== 1 && <span className="mv-shimmer rounded-sm h-[11px]" style={{ width: `${30 + ((i * 17) % 40)}%` }} />}
            </span>
          </div>
        ))}
        {!loading && lines.length === 0 && <div className="now-row text-small text-n8" style={{ cursor: 'default' }}><span /><span /><span>Nothing moved in the last 24 hours.</span></div>}
        {lines.map((line) => {
          const label = line.pinned ? null : dayLabel(line.at, now);
          const sep = label && label !== day ? label : null;
          if (sep) day = sep;
          return (
            <div key={line.key} className="contents">
              {sep && <div className="now-day"><Kicker>{sep}</Kicker></div>}
              <div role="button" tabIndex={0} className={cx('now-row', isNew(line.key) && 'is-new')} onClick={() => onOpen(line)} onKeyDown={keyActivate(line)}>
                <time dateTime={dayjs(line.at).toISOString()}>{line.pinned && now - line.at < 60_000 ? 'now' : dayjs(line.at).format('HH:mm')}</time>
                <span className="now-noun">
                  <NounIcon kind={line.kind} size={18} />
                  {line.tone && <i className={DOT[line.tone]} aria-hidden="true" />}
                </span>
                <span className="now-text"><LineText line={line} onThing={onThing} /></span>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

/**
 * Below the feed's breakpoint: a strip with the newest line; a press opens the whole feed as a drawer from the
 * bottom (scrim and Esc close it; opening a line closes it too).
 */
export function FeedTicker({ lines, now, loading, where, onOpen, onThing }: { lines: FeedLine[]; now: number; loading?: boolean; where?: string; onOpen: (line: FeedLine) => void; onThing?: (about: FeedAbout) => void }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const line = lines[0];
  return (
    <>
      <button type="button" className="now-ticker" aria-expanded={open} aria-label="Just happened" onClick={() => setOpen(true)}>
        <i className={cx('size-1.5 rounded-full shrink-0', loading ? 'bg-n5' : 'bg-ok now-live')} aria-hidden="true" />
        {line ? (
          <>
            <time dateTime={dayjs(line.at).toISOString()} className="font-mono text-micro text-n7 shrink-0">{dayjs(line.at).format('HH:mm')}</time>
            <NounIcon kind={line.kind} size={16} />
            <span className="now-ticker-text">{line.parts.map((p, i) => (typeof p === 'string' ? p : <span key={i} className="mono">{p.mono}</span>))}</span>
          </>
        ) : <span className="now-ticker-text text-n8">{loading ? '' : 'Nothing moved in the last 24 hours.'}</span>}
        <span className="font-mono text-micro text-n7 shrink-0">{lines.length > 1 ? `+${lines.length - 1}` : ''}</span>
      </button>
      {open && (
        <>
          <div className="now-drawer-scrim" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="now-drawer" role="dialog" aria-label="Just happened">
            <Feed lines={lines} now={now} loading={loading} where={where} onOpen={(l) => { setOpen(false); onOpen(l); }} onThing={onThing && ((a) => { setOpen(false); onThing(a); })} />
          </div>
        </>
      )}
    </>
  );
}

const PIN_KEY = 'mv.now.feedPinned';
function readPinned(): boolean {
  try { return localStorage.getItem(PIN_KEY) === '1'; } catch { return false; }
}

/**
 * The feed on a wide screen: pinned open (a choice the browser remembers), or folded to a tab at the right edge that
 * opens the feed while it is pointed at or focused, over the map, and folds it again a moment after the pointer leaves.
 * The page reads `pinned` to give the map the room (data-feed on its root).
 */
export function useFeedPin(): [boolean, () => void] {
  const [pinned, setPinned] = useState(readPinned);
  const toggle = () => setPinned((on) => {
    try { localStorage.setItem(PIN_KEY, on ? '0' : '1'); } catch { /* a preference only */ }
    return !on;
  });
  return [pinned, toggle];
}

export function FeedDock({ pinned, onPin, lines, ...feed }: Parameters<typeof Feed>[0] & { pinned: boolean; onPin: () => void }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(0);
  const show = () => { window.clearTimeout(timer.current); setOpen(true); };
  const hideSoon = () => { window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setOpen(false), 280); };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  if (pinned) return <Feed lines={lines} {...feed} pin={{ on: true, toggle: onPin }} />;
  const problems = lines.filter((l) => l.pinned && (l.tone === 'fail' || l.tone === 'warn'));
  const tone = problems.some((l) => l.tone === 'fail') ? 'fail' : problems.length ? 'warn' : null;
  return (
    <div className="now-feed-dock" data-open={open ? '' : undefined} onPointerEnter={show} onPointerLeave={hideSoon} onFocus={show}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) hideSoon(); }}>
      {open ? (
        <Feed lines={lines} {...feed} pin={{ on: false, toggle: () => { onPin(); setOpen(false); } }} />
      ) : (
        <button type="button" className="now-feed-tab" aria-label={`Just happened${problems.length ? `, ${problems.length} going on now` : ''}: open the feed`} onClick={show} data-feed-tab="">
          <i className={cx('size-1.5 rounded-full shrink-0', feed.loading ? 'bg-n5' : 'bg-ok now-live')} aria-hidden="true" />
          <PulseIcon size={16} />
          <span className="now-feed-tab-text">Just happened</span>
          {tone && <i className={cx('size-2 rounded-full shrink-0', tone === 'fail' ? 'bg-fail' : 'bg-warn')} aria-hidden="true" />}
          {problems.length > 0 && <span className="font-mono text-micro tabular-nums">{problems.length}</span>}
        </button>
      )}
    </div>
  );
}
